import { apply, celebrate, give, toast, type Delta } from '../core/effects';
import { bus } from '../core/bus';
import { S } from '../core/game';
import { dayId, type DayId, type GameState, type ItemKey } from '../core/state';
import { m, type Messages } from '../i18n';
import { px, type MapId, type ObjectId } from '../world/mapdef';
import type { NpcId } from '../world/npcs';

export type QuestId = keyof Messages['quests']['list'];

/** What the guide arrow points at. */
export type TargetRef = { kind: 'npc'; id: NpcId } | { kind: 'obj'; id: ObjectId } | { kind: 'pos'; map: MapId; x: number; y: number };

export interface QuestDef {
  id: QuestId;
  /** Days on which the quest can be started. */
  days: DayId[];
  /** Extra condition to offer the quest. */
  requires?: (s: GameState) => boolean;
  /** Target for the current step. */
  target: (step: number, s: GameState) => TargetRef | null;
  reward: Delta & { items?: Partial<Record<ItemKey, number>> };
}

/** Lucky clovers hidden around the world. */
export const CLOVERS: readonly { map: MapId; x: number; y: number }[] = [
  { map: 'town', x: px(7) + 4, y: px(41) },
  { map: 'town', x: px(50) + 8, y: px(11) },
  { map: 'town', x: px(53) + 2, y: px(44) },
  { map: 'town', x: px(1) + 10, y: px(57) },
  { map: 'town', x: px(19) + 6, y: px(8) + 4 },
  { map: 'school', x: px(13) + 4, y: px(27) + 6 },
  { map: 'school', x: px(42), y: px(3) },
  { map: 'home', x: px(22) + 6, y: px(6) + 6 },
];

/** Running track checkpoints for the coach's challenge (in order). */
export const LAP_POINTS: readonly { x: number; y: number }[] = [
  { x: px(3), y: px(10) },
  { x: px(18) + 8, y: px(10) },
  { x: px(18) + 8, y: px(19) },
  { x: px(3), y: px(19) },
];
export const LAP_TIME = 60;
export const KITTEN_BUSHES: readonly ObjectId[] = ['bush1', 'bush2', 'bush3'];

export const QUESTS: readonly QuestDef[] = [
  {
    id: 'atlas',
    days: ['fri'],
    target: (step) => (step === 0 ? { kind: 'npc', id: 'librarian' } : step === 1 ? { kind: 'obj', id: 'atlasDesk' } : { kind: 'npc', id: 'librarian' }),
    reward: { stars: 10, coins: 20, study: 8 },
  },
  {
    id: 'riceBall',
    days: ['fri'],
    target: (step) => (step === 1 ? { kind: 'npc', id: 'clerk' } : { kind: 'npc', id: 'chunbo' }),
    reward: { stars: 8, love: { chunbo: 20 } },
  },
  {
    id: 'laps',
    days: ['fri'],
    target: (step, s) => {
      if (step !== 1) return { kind: 'npc', id: 'coach' };
      const i = s.quests.laps?.data ?? 0;
      const p = LAP_POINTS[i % LAP_POINTS.length];
      return { kind: 'pos', map: 'town', ...p };
    },
    reward: { stars: 10, mood: 10, energy: -5 },
  },
  {
    id: 'study',
    days: ['fri'],
    target: (step) => (step === 1 ? { kind: 'obj', id: 'libShelf' } : { kind: 'npc', id: 'rain' }),
    reward: { stars: 6, study: 10 },
  },
  {
    id: 'kitten',
    days: ['sat', 'sun'],
    target: (step, s) => {
      if (step !== 1) return { kind: 'npc', id: 'grandma' };
      // Point at the park, not at the right bush — finding it is the game.
      void s;
      return { kind: 'pos', map: 'town', x: px(32), y: px(40) };
    },
    reward: { coins: 30, mood: 12, stars: 6 },
  },
  {
    id: 'kite',
    days: ['sat', 'sun'],
    target: (step) => (step === 1 ? { kind: 'obj', id: 'kiteTree' } : { kind: 'npc', id: 'bo' }),
    reward: { stars: 6, mood: 8, items: { candy: 2 } },
  },
  {
    id: 'xiaoyuPicnic',
    days: ['sat'],
    requires: (s) => !!s.forever.xiaoyuHelped,
    target: (step) => (step === 1 ? { kind: 'npc', id: 'emma' } : { kind: 'npc', id: 'xiaoyu' }),
    reward: { stars: 12, mood: 10, love: { xiaoyu: 20, emma: 10 } },
  },
  {
    id: 'letter',
    days: ['sun'],
    target: (step) => (step === 1 ? { kind: 'npc', id: 'grandma' } : { kind: 'npc', id: 'mom' }),
    reward: { stars: 6, love: { mom: 10 }, coins: 10 },
  },
  // --- Story chapters: errands that cross the town, Taipei and Hualien ------------------
  {
    id: 'postcards',
    days: ['fri', 'sat', 'sun'],
    target: (step, s) => {
      if (step === 1) {
        const got = s.quests.postcards?.data ?? 0;
        const givers: NpcId[] = ['guide', 'xm_volunteer', 'ck_guide'];
        const next = givers.find((_, i) => !(got & (1 << i)));
        return next ? { kind: 'npc', id: next } : { kind: 'npc', id: 'grandma' };
      }
      return { kind: 'npc', id: 'grandma' };
    },
    reward: { stars: 20, coins: 40, mood: 10 },
  },
  {
    id: 'pineapple',
    days: ['fri', 'sat', 'sun'],
    target: (step) => (step === 1 ? { kind: 'npc', id: 'xy_clerk' } : { kind: 'npc', id: 'mom' }),
    reward: { stars: 12, love: { mom: 15 }, mood: 6 },
  },
  {
    id: 'elephant',
    days: ['fri', 'sat', 'sun'],
    target: (step) => (step === 1 ? { kind: 'npc', id: 'xy_hiker' } : { kind: 'npc', id: 'sister' }),
    reward: { stars: 12, love: { sister: 20 }, mood: 8 },
  },
  {
    id: 'mochi',
    days: ['sat', 'sun'],
    target: (step) => (step === 1 ? { kind: 'npc', id: 'tk_mochiAuntie' } : { kind: 'npc', id: 'bo' }),
    reward: { stars: 14, coins: 20, mood: 8 },
  },
  {
    id: 'pebble',
    days: ['fri'],
    target: (step) => (step === 1 ? { kind: 'npc', id: 'tk_fisher' } : { kind: 'npc', id: 'rain' }),
    reward: { stars: 14, study: 8, mood: 6 },
  },
  {
    id: 'clovers',
    days: ['fri', 'sat', 'sun'],
    target: (_step, s) => {
      const left = CLOVERS.findIndex((_, i) => !s.forever[`clover.${i}`]);
      return left < 0 ? null : { kind: 'pos', ...CLOVERS[left] };
    },
    reward: { stars: 25, coins: 40 },
  },
];

export function questDef(id: QuestId): QuestDef {
  return QUESTS.find((q) => q.id === id)!;
}

export function questText(id: QuestId): Messages['quests']['list'][QuestId] {
  return m().quests.list[id];
}

export function step(id: QuestId): number {
  return S().quests[id]?.step ?? -1;
}

export function isActive(id: QuestId): boolean {
  const q = S().quests[id];
  return !!q && !q.done;
}

export function isDone(id: QuestId): boolean {
  return !!S().quests[id]?.done;
}

export function canStart(id: QuestId): boolean {
  const s = S();
  const def = questDef(id);
  return !s.quests[id] && def.days.includes(dayId(s)) && (def.requires?.(s) ?? true);
}

export function startQuest(id: QuestId, data?: number): void {
  const s = S();
  s.quests[id] = { step: 1, done: false, data };
  if (!s.tracked || !isActive(s.tracked as QuestId)) s.tracked = id;
  toast(`📜 ${m().quests.started}: ${questText(id).title}`);
  bus.emit('change');
}

export function advance(id: QuestId, to?: number): void {
  const q = S().quests[id];
  if (!q || q.done) return;
  q.step = to ?? q.step + 1;
  const steps = questText(id).steps;
  toast(`📜 ${steps[Math.min(q.step, steps.length - 1)]}`);
  bus.emit('change');
}

export function completeQuest(id: QuestId): void {
  const s = S();
  const q = s.quests[id] ?? (s.quests[id] = { step: 0, done: false });
  if (q.done) return;
  q.done = true;
  const r = questDef(id).reward;
  const { items, ...delta } = r;
  window.setTimeout(() => {
    celebrate(true);
    apply(delta);
    for (const [k, n] of Object.entries(items ?? {}) as [ItemKey, number][]) give(k, n);
    toast(`🏅 ${m().quests.completed}: ${questText(id).title}`);
  }, 400);
  if (s.tracked === id) s.tracked = QUESTS.find((d) => isActive(d.id))?.id ?? null;
  bus.emit('change');
}

/** Quests visible in the log: active ones first, then available, then done. */
export function questList(s: GameState = S()): { id: QuestId; state: 'active' | 'available' | 'done' }[] {
  const out: { id: QuestId; state: 'active' | 'available' | 'done' }[] = [];
  for (const q of QUESTS) {
    const st = s.quests[q.id];
    if (st && !st.done) out.push({ id: q.id, state: 'active' });
  }
  for (const q of QUESTS) if (!s.quests[q.id] && q.days.includes(dayId(s)) && (q.requires?.(s) ?? true)) out.push({ id: q.id, state: 'available' });
  for (const q of QUESTS) if (s.quests[q.id]?.done) out.push({ id: q.id, state: 'done' });
  return out;
}

export function cloversFound(s: GameState = S()): number {
  return CLOVERS.filter((_, i) => s.forever[`clover.${i}`]).length;
}

/** Pick up a clover. The clover quest starts with the first one and pays out at the last. */
export function collectClover(i: number): void {
  const s = S();
  if (s.forever[`clover.${i}`]) return;
  s.forever[`clover.${i}`] = true;
  if (!s.quests.clovers) s.quests.clovers = { step: 1, done: false };
  const n = cloversFound(s);
  toast(m().quests.clover(n, CLOVERS.length));
  apply({ mood: 3 }, { float: false });
  if (n === CLOVERS.length) completeQuest('clovers');
  bus.emit('change');
}
